import { useSchoolTrackingSheets } from '@/hooks/useSchoolTrackingSheets'
import { SchoolSwitcher } from '@/components/school/SchoolSwitcher'

function formatDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })
}

// Read-only: schools view a tracking sheet, they never edit it (that stays the owning
// instructor's own job, relationship_progress_sheets' "instructor own" policy, migration
// 066). Only sheets for a student this school actually assigned appear here — a private
// instructor's own students are invisible, by the RLS policy itself (migration 069), not
// by anything this page filters.
export default function TrackingSheets() {
  const { sheets, loading, error, refresh } = useSchoolTrackingSheets()

  return (
    <div>
      <div style={{ font: '600 26px Lora, serif', color: '#1B2A4A', marginBottom: 6 }}>
        Tracking Sheets
      </div>
      <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginBottom: 6 }}>
        In-car progress sheets for students assigned to your instructors — read-only; the instructor keeps the sheet up to date.
      </div>

      <SchoolSwitcher />

      {loading && (
        <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', padding: '24px 0' }}>
          Loading…
        </div>
      )}

      {error && !loading && (
        <div className='banner-error' style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14 }}>
          <span>{error}</span>
          <button
            onClick={refresh}
            style={{ background: 'none', border: 'none', font: '600 12.5px Inter, sans-serif', color: '#0F7C6C', cursor: 'pointer', flexShrink: 0 }}
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && sheets.length === 0 && (
        <div style={{
          background: '#fff', border: '1px solid rgba(27,42,74,0.08)',
          borderRadius: 14, padding: '36px 28px', textAlign: 'center',
        }}>
          <div style={{ font: '600 16px Lora, serif', color: '#1B2A4A', marginBottom: 8 }}>
            No tracking sheets yet
          </div>
          <div style={{ font: '400 13.5px Inter, sans-serif', color: 'rgba(27,42,74,0.5)' }}>
            A sheet appears here once an instructor starts logging in-car progress for a student your school assigned.
          </div>
        </div>
      )}

      {!loading && !error && sheets.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14, overflow: 'hidden' }}>
          <div className='dt-head' style={{ gridTemplateColumns: 'minmax(0,1.6fr) minmax(0,1.6fr) minmax(0,1fr) minmax(0,1.2fr)' }}>
            <span>Student</span>
            <span>Instructor</span>
            <span>A–H items</span>
            <span>Completed</span>
          </div>

          {sheets.map((s, i) => (
            <div
              key={s.relationshipId}
              className='dt-row-desk'
              style={{
                gridTemplateColumns: 'minmax(0,1.6fr) minmax(0,1.6fr) minmax(0,1fr) minmax(0,1.2fr)',
                borderBottom: i === sheets.length - 1 ? 'none' : '1px solid rgba(27,42,74,0.05)',
              }}
            >
              <div style={{ font: '600 13.5px Inter, sans-serif', color: '#1B2A4A' }}>{s.studentName ?? '—'}</div>
              <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.6)' }}>{s.instructorName ?? '—'}</div>
              <div style={{ font: '500 13px Inter, sans-serif', color: 'rgba(27,42,74,0.6)' }}>{s.itemsCompleted} / 8</div>
              <div style={{ font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.5)' }}>{formatDate(s.completionDate)}</div>
            </div>
          ))}
        </div>
      )}

      {!loading && sheets.length > 0 && (
        <div style={{ marginTop: 14, font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', textAlign: 'right' }}>
          {sheets.length} tracking sheet{sheets.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  )
}
